import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import CheckoutForm from '../../CheckoutForm/CheckoutForm';
import SubscriptionMarketSelector from '../../SubscriptionMarketSelector/SubscriptionMarketSelector';
import { fetchSubscriptionPlans } from '../../../services/payment.service';
import { createAnalyticsClientEventId, postAnalyticsEvent } from '../../../services/analytics.service';
import { normalizeUserInfo, useUserService } from '../../../services/user.service';
import { trackMarketingConversion } from '../../../services/marketing.service';
import { useUserState } from '../../../providers/user-state.provider.ts';
import { useSubscriptionStatus } from '../../../providers/subscription-status.provider';
import { ANALYTICS_EVENT_TYPES } from '../../../types/analytics.types';
import { Routes } from '../../../types/constants';
import type { SubscriptionPlan } from '../../../types/payment.types';
import type { UserInfo, UserRegistrationResponse } from '../../../types/user.types';
import { useSubscriptionMarket } from '../../../hooks/useSubscriptionMarket';
import { formatSubscriptionPrice, sanitizePlans } from '../../../utils/subscription-plans';
import { isSubscriptionCheckoutRecoverable, requiresSubscriptionManagement } from '../../../utils/subscription';
import {
    buildJoinUrl,
    getOrCreateJoinCheckoutAttempt,
    getSafeInternalReturnTo,
    getSafeReturnTo,
    normalizeJoinEntryPoint,
    replaceJoinCheckoutAttempt,
    storeJoinCheckoutAttempt
} from '../../../utils/join';
import styles from './Join.module.scss';

const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{6,256}$/;
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type AccountMode = 'register' | 'sign_in';
type AccountErrors = { email?: string; password?: string };
type AccountConfirmation = 'created' | 'signed_in';

const readStoredRedirectAfterLogin = (): string | null => {
    try {
        return window.localStorage.getItem('redirectAfterLogin');
    } catch {
        // Legacy checkout redirects remain usable when mobile privacy settings block storage.
        return null;
    }
};

const getApiErrorData = (error: unknown): Record<string, unknown> => {
    if (!error || typeof error !== 'object') return {};
    const candidate = error as { data?: unknown; response?: unknown };
    const value = candidate.data ?? candidate.response;
    if (value && typeof value === 'object') {
        const nested = value as { data?: unknown };
        if (nested.data && typeof nested.data === 'object') return nested.data as Record<string, unknown>;
        return value as Record<string, unknown>;
    }
    return {};
};

const getApiErrorCode = (error: unknown) => {
    const data = getApiErrorData(error);
    const value = data.code ?? data.Code;
    return typeof value === 'string' ? value.toLowerCase() : '';
};

const getApiErrorStatus = (error: unknown) => (
    typeof (error as { status?: unknown })?.status === 'number'
        ? (error as { status: number }).status
        : undefined
);

const getApiErrorMessage = (error: unknown, fallback: string) => {
    const data = getApiErrorData(error);
    const value = data.detail ?? data.Detail ?? data.message ?? data.Message;
    if (typeof value === 'string' && value.trim()) return value;
    return error instanceof Error && error.message ? error.message : fallback;
};

const isExistingAccountError = (error: unknown) => {
    const code = getApiErrorCode(error);
    const status = typeof (error as { status?: unknown })?.status === 'number'
        ? (error as { status: number }).status
        : undefined;
    if (status !== 409) return false;
    if (code) {
        return code === 'existing_account'
            || code === 'duplicate_email'
            || code === 'duplicate_user';
    }
    return getApiErrorMessage(error, '').toLowerCase().includes('already');
};

const getIntervalLabel = (plan: SubscriptionPlan) => {
    const interval = plan.recurringInterval.toLowerCase();
    return plan.recurringIntervalCount === 1
        ? interval
        : `${plan.recurringIntervalCount} ${interval}s`;
};

const Join = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { registerForCheckout, login } = useUserService();
    const { clearUserInfo, getUserInfo, setUserInfo, isLoggedIn, isUserStateLoading } = useUserState();
    const {
        hasActiveSubscription,
        isSubscriptionLoading,
        subscription
    } = useSubscriptionStatus();

    useLayoutEffect(() => {
        // App renders shared chrome outside the router, so checkout owns its distraction-free shell here.
        document.body.classList.add('join-checkout-active');
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        return () => document.body.classList.remove('join-checkout-active');
    }, []);

    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [selectedOfferingId, setSelectedOfferingId] = useState('');
    const [plansStatus, setPlansStatus] = useState<'loading' | 'ready' | 'error'>('loading');
    const [plansReload, setPlansReload] = useState(0);
    const [checkoutAttemptId, setCheckoutAttemptId] = useState('');
    const [paymentSessionGeneration, setPaymentSessionGeneration] = useState(0);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [accountMode, setAccountMode] = useState<AccountMode>('register');
    const [accountErrors, setAccountErrors] = useState<AccountErrors>({});
    const [accountError, setAccountError] = useState<string | null>(null);
    const [accountConfirmation, setAccountConfirmation] = useState<AccountConfirmation | null>(null);
    const [isAccountSubmitting, setIsAccountSubmitting] = useState(false);
    const [isCheckoutSubmitting, setIsCheckoutSubmitting] = useState(false);
    const [hasCheckoutStarted, setHasCheckoutStarted] = useState(false);
    const [initialAccountWasLoggedIn, setInitialAccountWasLoggedIn] = useState<boolean | null>(null);
    const [initialSubscriptionCheckCompleted, setInitialSubscriptionCheckCompleted] = useState(false);
    const emailRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);
    const trackedCheckoutViews = useRef(new Set<string>());
    const requestedPlanId = searchParams.get('plan')?.trim() ?? '';
    const requestedMarketCode = searchParams.get('market')?.trim() ?? '';
    const entryPoint = normalizeJoinEntryPoint(searchParams.get('source'));
    const returnTo = getSafeReturnTo(searchParams.get('returnTo'));
    const hasRecoverableCheckout = isSubscriptionCheckoutRecoverable(subscription);
    const hasManagedSubscription = requiresSubscriptionManagement(subscription);
    const recoveryMarketCode = hasRecoverableCheckout ? subscription?.PlanMarketCode : null;
    const {
        markets,
        selectedMarket,
        selectMarket,
        reloadMarkets,
        status: marketStatus
    } = useSubscriptionMarket(recoveryMarketCode ?? requestedMarketCode);
    const selectedMarketCode = selectedMarket?.code ?? '';
    const recoverablePlan = hasRecoverableCheckout
        ? plans.find((plan) => (
            (subscription?.PlanOfferingId && plan.offeringId === subscription.PlanOfferingId)
            || (!subscription?.PlanOfferingId
                && plan.recurringInterval.toLowerCase() === subscription?.PlanInterval?.trim().toLowerCase())
        )) ?? null
        : null;
    const isRecoverablePlanLocked = recoverablePlan !== null;
    const selectedOffering = plans.find((plan) => plan.offeringId === selectedOfferingId) ?? null;
    const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
    const currentUser = getUserInfo();
    const currentUserId = currentUser?.userId;
    const stripePromise = useMemo(
        () => stripePublicKey && currentUserId ? loadStripe(stripePublicKey) : null,
        [currentUserId, stripePublicKey]
    );
    const isInteractionLocked = isAccountSubmitting || isCheckoutSubmitting;

    useEffect(() => {
        if (!isUserStateLoading && initialAccountWasLoggedIn === null) {
            setInitialAccountWasLoggedIn(isLoggedIn);
        }
    }, [initialAccountWasLoggedIn, isLoggedIn, isUserStateLoading]);

    useEffect(() => {
        if (initialAccountWasLoggedIn === false
            || (initialAccountWasLoggedIn === true && !isSubscriptionLoading)) {
            setInitialSubscriptionCheckCompleted(true);
        }
    }, [initialAccountWasLoggedIn, isSubscriptionLoading]);

    useEffect(() => {
        if (marketStatus === 'error') {
            setPlans([]);
            setSelectedOfferingId('');
            setPlansStatus('error');
            return;
        }
        if (marketStatus !== 'ready' || !selectedMarketCode) return;

        let active = true;
        setPlansStatus('loading');
        fetchSubscriptionPlans(selectedMarketCode)
            .then((response) => {
                if (!active) return;
                const authoritativePlans = sanitizePlans(response);
                if (!authoritativePlans.length) throw new Error('No subscription plans are currently available.');
                setPlans(authoritativePlans);
                setPlansStatus('ready');
            })
            .catch(() => {
                if (!active) return;
                setPlans([]);
                setSelectedOfferingId('');
                setPlansStatus('error');
            });
        return () => {
            active = false;
        };
    }, [marketStatus, plansReload, selectedMarketCode]);

    useEffect(() => {
        if (plansStatus !== 'ready' || !plans.length || isInteractionLocked) return;
        const requestedPlan = plans.find((plan) => (
            plan.offeringId === requestedPlanId
            || plan.planCode.toLowerCase() === requestedPlanId.toLowerCase()
        ));
        const currentPlan = plans.find((plan) => plan.offeringId === selectedOfferingId);
        const monthlyPlan = plans.find((plan) => plan.recurringInterval.toLowerCase() === 'month');
        const nextPlan = recoverablePlan ?? requestedPlan ?? currentPlan ?? monthlyPlan ?? plans[0];

        if (nextPlan.offeringId !== selectedOfferingId) {
            if (recoverablePlan && checkoutAttemptId) {
                setCheckoutAttemptId(replaceJoinCheckoutAttempt(nextPlan.offeringId));
            } else if (checkoutAttemptId) {
                storeJoinCheckoutAttempt(nextPlan.offeringId, checkoutAttemptId);
            }
            setSelectedOfferingId(nextPlan.offeringId);
        }
        if (requestedPlanId !== nextPlan.offeringId
            || requestedMarketCode.toLowerCase() !== selectedMarketCode.toLowerCase()) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.set('plan', nextPlan.offeringId);
            nextParams.set('market', selectedMarketCode.toLowerCase());
            setSearchParams(nextParams, { replace: true });
        }
    }, [
        isInteractionLocked,
        checkoutAttemptId,
        plans,
        plansStatus,
        recoverablePlan,
        requestedMarketCode,
        requestedPlanId,
        searchParams,
        selectedMarketCode,
        selectedOfferingId,
        setSearchParams
    ]);

    useEffect(() => {
        if (!selectedOfferingId || checkoutAttemptId) return;
        setCheckoutAttemptId(getOrCreateJoinCheckoutAttempt(selectedOfferingId));
    }, [checkoutAttemptId, selectedOfferingId]);

    useEffect(() => {
        if (isUserStateLoading
            || (isLoggedIn && isSubscriptionLoading)
            || (isLoggedIn && (hasActiveSubscription || (hasManagedSubscription && !hasRecoverableCheckout)))
            || !selectedOffering
            || !checkoutAttemptId
            || trackedCheckoutViews.current.has(checkoutAttemptId)) return;
        trackedCheckoutViews.current.add(checkoutAttemptId);
        void postAnalyticsEvent({
            eventType: ANALYTICS_EVENT_TYPES.SUBSCRIPTION_CHECKOUT_VIEWED,
            orderId: checkoutAttemptId,
            value: selectedOffering.price,
            currency: selectedOffering.currency.toUpperCase(),
            metadata: {
                offeringId: selectedOffering.offeringId,
                marketCode: selectedOffering.marketCode,
                entryPoint
            }
        }, currentUser?.token);
    }, [
        checkoutAttemptId,
        currentUser?.token,
        entryPoint,
        hasActiveSubscription,
        hasManagedSubscription,
        hasRecoverableCheckout,
        isLoggedIn,
        isSubscriptionLoading,
        isUserStateLoading,
        selectedOffering
    ]);

    useEffect(() => {
        if (isUserStateLoading || isSubscriptionLoading || !isLoggedIn) return;
        if (hasActiveSubscription) {
            navigate(returnTo, { replace: true });
        } else if (hasManagedSubscription && !hasRecoverableCheckout) {
            navigate(Routes.PROFILE_BILLING, { replace: true });
        }
    }, [
        hasActiveSubscription,
        hasManagedSubscription,
        hasRecoverableCheckout,
        isLoggedIn,
        isSubscriptionLoading,
        isUserStateLoading,
        navigate,
        returnTo
    ]);

    const validateAccount = useCallback(() => {
        if (getUserInfo()) return true;
        const nextErrors: AccountErrors = {};
        const normalizedEmail = email.trim();
        if (!normalizedEmail) {
            nextErrors.email = accountMode === 'register'
                ? 'Enter your email address.'
                : 'Enter your email or username.';
        } else if (accountMode === 'register' && !emailPattern.test(normalizedEmail)) {
            nextErrors.email = 'Enter a valid email address.';
        }
        if (!password) {
            nextErrors.password = 'Enter your password.';
        } else if (accountMode === 'register' && !passwordPattern.test(password)) {
            nextErrors.password = 'Use 6 or more characters with upper and lowercase letters, a number and a symbol.';
        }
        setAccountErrors(nextErrors);
        setAccountError(null);
        if (nextErrors.email) emailRef.current?.focus();
        else if (nextErrors.password) passwordRef.current?.focus();
        return Object.keys(nextErrors).length === 0;
    }, [accountMode, email, getUserInfo, password]);

    const ensureAuthenticated = useCallback(async (
        activeCheckoutAttemptId: string
    ): Promise<UserInfo & { checkoutAttemptId?: string }> => {
        const existingUser = getUserInfo();
        if (existingUser) return existingUser;
        if (!validateAccount() || !selectedOffering) {
            throw new Error('Check your account details and try again.');
        }

        setIsAccountSubmitting(true);
        setAccountError(null);
        try {
            if (accountMode === 'sign_in') {
                try {
                    const signedInUser = normalizeUserInfo(await login(
                        { username: email.trim(), password },
                        { suppressAuthModalOnUnauthorized: true }
                    ));
                    if (!signedInUser.userId || !signedInUser.token) {
                        throw new Error('Sign in did not return a valid session.');
                    }
                    setAccountConfirmation('signed_in');
                    setUserInfo(signedInUser);
                    return signedInUser;
                } catch (error) {
                    const isAuthenticationFailure = getApiErrorStatus(error) === 401;
                    const message = isAuthenticationFailure
                        ? 'Email or password is incorrect. Check your details and try again.'
                        : 'Sign in is temporarily unavailable. Please try again.';
                    setAccountError(message);
                    if (isAuthenticationFailure) {
                        passwordRef.current?.focus();
                    }
                    throw new Error(message);
                }
            }

            const signupEventId = createAnalyticsClientEventId();
            void postAnalyticsEvent({
                eventType: ANALYTICS_EVENT_TYPES.SIGNUP_STARTED,
                clientEventId: signupEventId,
                orderId: activeCheckoutAttemptId,
                metadata: {
                    source: 'pay_and_play_checkout',
                    offeringId: selectedOffering.offeringId,
                    marketCode: selectedOffering.marketCode,
                    entryPoint
                }
            });
            const response: UserRegistrationResponse = await registerForCheckout({
                email: email.trim(),
                password,
                offeringId: selectedOffering.offeringId,
                checkoutAttemptId: activeCheckoutAttemptId,
                entryPoint
            }, signupEventId);
            if (response.accountStatus === 'existing') {
                if (response.checkoutAttemptId && response.checkoutAttemptId !== activeCheckoutAttemptId) {
                    setCheckoutAttemptId(response.checkoutAttemptId);
                    storeJoinCheckoutAttempt(selectedOffering.offeringId, response.checkoutAttemptId);
                }
                const message = 'An account already uses this email. Sign in or use another email.';
                setAccountError(message);
                emailRef.current?.focus();
                throw new Error(message);
            }
            setAccountConfirmation('created');
            setUserInfo(response);
            if (response.accountStatus === 'created') {
                trackMarketingConversion({
                    kind: 'signup',
                    eventId: signupEventId,
                    metadata: { source: 'pay_and_play_checkout' }
                });
            }
            return response;
        } catch (error) {
            if (getApiErrorCode(error) === 'checkout_restart_required') {
                throw error;
            }
            if (accountMode === 'register' && isExistingAccountError(error)) {
                const message = 'An account already uses this email. Sign in or use another email.';
                setAccountError(message);
                emailRef.current?.focus();
                throw new Error(message);
            }
            const message = getApiErrorMessage(error, 'We could not create your account. Check your details and try again.');
            setAccountError(message);
            throw error;
        } finally {
            setIsAccountSubmitting(false);
        }
    }, [
        accountMode,
        email,
        entryPoint,
        getUserInfo,
        login,
        password,
        registerForCheckout,
        selectedOffering,
        setUserInfo,
        validateAccount
    ]);

    const handleAccountSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isInteractionLocked || !checkoutAttemptId || !selectedOffering) return;

        try {
            const authenticatedUser = await ensureAuthenticated(checkoutAttemptId);
            if (authenticatedUser.checkoutAttemptId
                && authenticatedUser.checkoutAttemptId !== checkoutAttemptId) {
                setCheckoutAttemptId(authenticatedUser.checkoutAttemptId);
                storeJoinCheckoutAttempt(selectedOffering.offeringId, authenticatedUser.checkoutAttemptId);
            }
        } catch (error) {
            if (getApiErrorCode(error) !== 'checkout_restart_required') return;
            const nextCheckoutAttemptId = replaceJoinCheckoutAttempt(selectedOffering.offeringId);
            setCheckoutAttemptId(nextCheckoutAttemptId);
            setAccountError(null);
        }
    };

    const handleAccountModeChange = (mode: AccountMode) => {
        if (isInteractionLocked || mode === accountMode) return;
        setAccountMode(mode);
        setAccountError(null);
        setAccountErrors({});
    };

    const handlePlanChange = (offering: SubscriptionPlan) => {
        if (isInteractionLocked
            || hasCheckoutStarted
            || isRecoverablePlanLocked
            || offering.offeringId === selectedOfferingId) return;
        setSelectedOfferingId(offering.offeringId);
        storeJoinCheckoutAttempt(offering.offeringId, checkoutAttemptId);
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('plan', offering.offeringId);
        nextParams.set('market', offering.marketCode.toLowerCase());
        setSearchParams(nextParams, { replace: true });
        void postAnalyticsEvent({
            eventType: ANALYTICS_EVENT_TYPES.SUBSCRIPTION_PLAN_SELECTED,
            orderId: checkoutAttemptId,
            value: offering.price,
            currency: offering.currency.toUpperCase(),
            metadata: {
                source: entryPoint,
                offeringId: offering.offeringId,
                marketCode: offering.marketCode
            }
        }, currentUser?.token);
    };

    const handleMarketChange = (marketCode: string) => {
        if (isInteractionLocked
            || hasCheckoutStarted
            || isRecoverablePlanLocked
            || marketCode.toLowerCase() === selectedMarketCode.toLowerCase()) return;

        const planCode = selectedOffering?.planCode ?? requestedPlanId;
        selectMarket(marketCode);
        setPlans([]);
        setSelectedOfferingId('');
        setPlansStatus('loading');
        setPaymentSessionGeneration((generation) => generation + 1);
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('market', marketCode.toLowerCase());
        if (planCode) nextParams.set('plan', planCode);
        setSearchParams(nextParams, { replace: true });
    };

    const handleUseAnotherAccount = () => {
        if (isInteractionLocked || !selectedOffering) return;
        clearUserInfo();
        const nextCheckoutAttemptId = replaceJoinCheckoutAttempt(selectedOffering.offeringId);
        setCheckoutAttemptId(nextCheckoutAttemptId);
        setPaymentSessionGeneration((generation) => generation + 1);
        setHasCheckoutStarted(false);
        setEmail('');
        setPassword('');
        setShowPassword(false);
        setAccountMode('register');
        setAccountErrors({});
        setAccountError(null);
        setAccountConfirmation(null);
    };

    const handleForgotPassword = () => {
        const joinReturnTo = getSafeInternalReturnTo(
            `${location.pathname}${location.search}${location.hash}`,
            Routes.JOIN
        );
        try {
            window.localStorage.setItem('redirectAfterLogin', joinReturnTo);
        } catch {
            // Password recovery remains available when browser storage is blocked.
        }
    };

    const elementsOptions = useMemo<StripeElementsOptions | null>(() => selectedOffering ? ({
        mode: 'subscription',
        amount: selectedOffering.unitAmount,
        currency: selectedOffering.currency,
        paymentMethodTypes: ['card'],
        appearance: {
            theme: 'stripe',
            variables: {
                colorPrimary: '#001ca8',
                colorText: '#0b0b2b',
                borderRadius: '10px',
                fontSizeBase: '16px',
                fontFamily: 'Open Sans, system-ui, sans-serif'
            }
        }
    }) : null, [selectedOffering]);

    const checkoutHeader = (
        <header className={styles.intro}>
            <button
                type="button"
                className={styles.exitCheckout}
                onClick={() => navigate(Routes.HOME)}
                disabled={isInteractionLocked}
                aria-label="Leave checkout"
            >
                <span className="material-symbols-outlined" aria-hidden="true">close</span>
            </button>
            <h1>Choose your plan and pay securely.</h1>
        </header>
    );

    if (marketStatus === 'loading'
        || plansStatus === 'loading'
        || initialAccountWasLoggedIn === null
        || !initialSubscriptionCheckCompleted) {
        return (
            <main className={styles.page} aria-busy="true">
                <div className={styles.shell}>
                    {checkoutHeader}
                    <div className={styles.loadingState} role="status">Preparing secure checkout…</div>
                </div>
            </main>
        );
    }

    return (
        <main className={styles.page}>
            <div className={styles.shell}>
                {checkoutHeader}

                {plansStatus === 'error' ? (
                    <section className={styles.unavailable} role="alert">
                        <h2>Plans are temporarily unavailable</h2>
                        <p>We could not verify current pricing, so checkout has not been opened.</p>
                        <button
                            type="button"
                            onClick={() => {
                                if (marketStatus === 'error') reloadMarkets();
                                else setPlansReload((value) => value + 1);
                            }}
                        >
                            Try again
                        </button>
                    </section>
                ) : null}

                {plansStatus === 'ready' && selectedOffering ? (
                    <section className={styles.checkout} aria-label="Join Combat TV">
                        {markets.length > 1 && selectedMarket ? (
                            <div className={styles.marketSelector}>
                                <SubscriptionMarketSelector
                                    markets={markets}
                                    selectedMarketCode={selectedMarket.code}
                                    disabled={isInteractionLocked
                                        || hasCheckoutStarted
                                        || isRecoverablePlanLocked}
                                    onChange={handleMarketChange}
                                />
                            </div>
                        ) : null}
                        <fieldset
                            className={styles.planSelector}
                            data-recovery-locked={isRecoverablePlanLocked}
                            aria-describedby={isRecoverablePlanLocked ? 'join-recovery-notice' : undefined}
                        >
                            <legend className={styles.srOnly}>Choose your plan</legend>
                            <div className={styles.planOptions}>
                                {plans.map((plan) => {
                                    const selected = plan.offeringId === selectedOffering.offeringId;
                                    return (
                                        <button
                                            key={plan.offeringId}
                                            type="button"
                                            className={selected ? styles.planSelected : styles.planOption}
                                            aria-pressed={selected}
                                            disabled={isInteractionLocked
                                                || hasCheckoutStarted
                                                || isRecoverablePlanLocked}
                                            onClick={() => handlePlanChange(plan)}
                                        >
                                            <span>{plan.duration}</span>
                                            <strong>{formatSubscriptionPrice(plan)}</strong>
                                            {plan.badge ? <small>{plan.badge}</small> : null}
                                        </button>
                                    );
                                })}
                            </div>
                        </fieldset>

                        {hasRecoverableCheckout ? (
                            <div id="join-recovery-notice" className={styles.notice} role="status">
                                Your earlier {selectedOffering.duration} payment is incomplete. We’ll safely resume
                                that plan when you submit.
                            </div>
                        ) : null}

                        {!currentUser ? (
                            <form
                                className={styles.accountSection}
                                aria-labelledby="join-account-heading"
                                onSubmit={handleAccountSubmit}
                            >
                                <div className={styles.modeSelector} aria-label="Account option">
                                    <button
                                        type="button"
                                        className={accountMode === 'register' ? styles.modeSelected : undefined}
                                        aria-label="Use create account"
                                        aria-pressed={accountMode === 'register'}
                                        disabled={isInteractionLocked}
                                        onClick={() => handleAccountModeChange('register')}
                                    >
                                        Create account
                                    </button>
                                    <button
                                        type="button"
                                        className={accountMode === 'sign_in' ? styles.modeSelected : undefined}
                                        aria-label="Use sign in"
                                        aria-pressed={accountMode === 'sign_in'}
                                        disabled={isInteractionLocked}
                                        onClick={() => handleAccountModeChange('sign_in')}
                                    >
                                        Sign in
                                    </button>
                                </div>
                                <div className={styles.sectionHeading}>
                                    <h2 id="join-account-heading">
                                        {accountMode === 'register' ? 'Create your account' : 'Sign into your account'}
                                    </h2>
                                </div>
                                {accountError ? <div className={styles.accountError} role="alert">{accountError}</div> : null}
                                <div className={styles.fields}>
                                    <label>
                                        {accountMode === 'register' ? 'Email' : 'Email or username'}
                                        <input
                                            ref={emailRef}
                                            type={accountMode === 'register' ? 'email' : 'text'}
                                            inputMode={accountMode === 'register' ? 'email' : 'text'}
                                            autoComplete={accountMode === 'register' ? 'email' : 'username'}
                                            value={email}
                                            onChange={(event) => {
                                                setEmail(event.target.value);
                                                setAccountErrors((current) => ({ ...current, email: undefined }));
                                                setAccountError(null);
                                            }}
                                            aria-invalid={Boolean(accountErrors.email)}
                                            aria-describedby={accountErrors.email ? 'join-email-error' : undefined}
                                            maxLength={256}
                                            disabled={isInteractionLocked}
                                        />
                                        {accountErrors.email ? <span id="join-email-error" className={styles.fieldError}>{accountErrors.email}</span> : null}
                                    </label>
                                    <div className={styles.passwordGroup}>
                                        <div className={styles.passwordLabelRow}>
                                            <label htmlFor="join-password">Password</label>
                                            {accountMode === 'sign_in' ? (
                                                <Link
                                                    className={styles.forgotPassword}
                                                    to={Routes.FORGOT_PASSWORD}
                                                    onClick={handleForgotPassword}
                                                >
                                                    Forgot password?
                                                </Link>
                                            ) : null}
                                        </div>
                                        <span className={styles.passwordField}>
                                            <input
                                                id="join-password"
                                                ref={passwordRef}
                                                type={showPassword ? 'text' : 'password'}
                                                autoComplete={accountMode === 'register' ? 'new-password' : 'current-password'}
                                                value={password}
                                                onChange={(event) => {
                                                    setPassword(event.target.value);
                                                    setAccountErrors((current) => ({ ...current, password: undefined }));
                                                    setAccountError(null);
                                                }}
                                                aria-invalid={Boolean(accountErrors.password)}
                                                aria-describedby={accountErrors.password
                                                    ? 'join-password-error'
                                                    : accountMode === 'register' ? 'join-password-help' : undefined}
                                                maxLength={256}
                                                disabled={isInteractionLocked}
                                            />
                                            <button
                                                type="button"
                                                disabled={isInteractionLocked}
                                                onClick={() => setShowPassword((value) => !value)}
                                            >
                                                {showPassword ? 'Hide' : 'Show'}
                                            </button>
                                        </span>
                                        {accountErrors.password ? (
                                            <span id="join-password-error" className={styles.fieldError}>{accountErrors.password}</span>
                                        ) : accountMode === 'register' ? (
                                            <span id="join-password-help" className={styles.fieldHelp}>
                                                6+ characters: upper/lowercase, a number and symbol.
                                            </span>
                                        ) : null}
                                    </div>
                                </div>
                                <button
                                    type="submit"
                                    className={styles.accountSubmit}
                                    disabled={isInteractionLocked || !checkoutAttemptId}
                                >
                                    {isAccountSubmitting
                                        ? accountMode === 'register' ? 'Creating your account…' : 'Signing you in…'
                                        : accountMode === 'register' ? 'Create account' : 'Sign in'}
                                </button>
                                <p className={styles.paymentPreview}>
                                    Apple Pay, Google Pay, and cards are available in the next step.
                                </p>
                            </form>
                        ) : (
                            <section className={styles.signedIn} aria-label="Account">
                                <div>
                                    <span>{accountConfirmation === 'created' ? 'Account created for' : 'Signed in as'}</span>
                                    <strong>{currentUser.email}</strong>
                                </div>
                                <button
                                    type="button"
                                    disabled={isInteractionLocked}
                                    onClick={handleUseAnotherAccount}
                                >
                                    Use another account
                                </button>
                            </section>
                        )}

                        {currentUser ? (
                            <>
                                <section className={styles.paymentSection} aria-labelledby="join-payment-heading">
                                    <div className={styles.sectionHeading}>
                                        <h2 id="join-payment-heading">Secure payment</h2>
                                    </div>
                                    {!stripePublicKey ? (
                                        <div className={styles.accountError} role="alert">
                                            Secure payment is temporarily unavailable. Please try again shortly.
                                        </div>
                                    ) : stripePromise && elementsOptions && checkoutAttemptId ? (
                                        <Elements
                                            key={`${selectedOffering.offeringId}:${paymentSessionGeneration}`}
                                            stripe={stripePromise}
                                            options={elementsOptions}
                                        >
                                            <CheckoutForm
                                                offering={selectedOffering}
                                                checkoutAttemptId={checkoutAttemptId}
                                                entryPoint={entryPoint}
                                                returnTo={returnTo}
                                                submitLabel="Start watching"
                                                amountLabel={formatSubscriptionPrice(selectedOffering)}
                                                recurrenceLabel={`Renews every ${getIntervalLabel(selectedOffering)}`}
                                                showCancelButton={false}
                                                expressCheckoutEnabled
                                                validateBeforePayment={validateAccount}
                                                ensureAuthenticated={ensureAuthenticated}
                                                onCheckoutAttemptChanged={(nextAttemptId) => {
                                                    setCheckoutAttemptId(nextAttemptId);
                                                    storeJoinCheckoutAttempt(selectedOffering.offeringId, nextAttemptId);
                                                }}
                                                onCheckoutStarted={() => setHasCheckoutStarted(true)}
                                                onSubmittingChange={setIsCheckoutSubmitting}
                                            />
                                        </Elements>
                                    ) : (
                                        <p className={styles.paymentLoading} role="status">Loading secure payment…</p>
                                    )}
                                </section>

                                <footer className={styles.summary}>
                                    <p>
                                        By subscribing, you authorise recurring charges until cancelled. Secure payment
                                        powered by Stripe.
                                    </p>
                                </footer>
                            </>
                        ) : null}
                    </section>
                ) : null}
            </div>
        </main>
    );
};

export const LegacyJoinRedirect = ({ source }: { source: 'legacy_register' | 'legacy_payment' }) => {
    const location = useLocation();
    const state = location.state as { offering?: SubscriptionPlan } | null;
    const params = new URLSearchParams(location.search);
    const returnTo = params.get('returnTo') ?? readStoredRedirectAfterLogin();
    return (
        <Navigate
            replace
            to={buildJoinUrl({
                offeringId: params.get('plan') ?? state?.offering?.offeringId,
                market: params.get('market') ?? state?.offering?.marketCode,
                source,
                returnTo
            })}
        />
    );
};

export default Join;
